import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_close } from 'react-icons-kit/md/ic_close';
import { jqPath } from './helpers';
import {
  CHIP,
  CHIP_WARNING,
  CHIP_DIVIDER,
  EMPTY_SLOT,
  EMPTY_SLOT_TEXT,
  DROP_ZONE_ACTIVE,
  DROP_ZONE_IDLE,
  DROP_ZONE_TARGET_HOT,
} from './styles';

// The `handlers` object useDragOver returns ({} in read-only mode).
export type DragOverHandlers = {
  onDragOver?: (e: React.DragEvent) => void;
  onDragLeave?: () => void;
};

// Chips show the path without jq's leading dot (`device.id`, `tags[0]`); the
// dotted form stays in the title for hover and for tests.
export const displayPath = (path: string[]) => jqPath(path).replace(/^\./, '');

// A mapped slot: `device.id "collar-0417"` with × and the wand on the right.
// The value is shown as JSON (strings keep their quotes) with no "=" sign.
export function MappingChip({
  path,
  previewValue,
  notInSample,
  readOnly,
  onClear,
  clearLabel,
  wand,
  showPreview = true,
}: {
  path: string[];
  previewValue?: unknown;
  notInSample: boolean;
  /** Conditions show only the path: their value lives in the row below. */
  showPreview?: boolean;
  readOnly: boolean;
  onClear: () => void;
  clearLabel: string;
  wand?: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={notInSample ? CHIP_WARNING : CHIP}>
      <span
        className="min-w-0 shrink truncate text-ui-text-secondary-text"
        style={{ direction: 'rtl', textAlign: 'left' }}
        title={jqPath(path)}
      >
        <bdi>{displayPath(path)}</bdi>
      </span>
      {!showPreview ? null : notInSample ? (
        <span className="whitespace-nowrap text-body-sm italic">
          {t('Not in schema')}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-greyscale-5">
          {JSON.stringify(previewValue)}
        </span>
      )}
      {!readOnly && (
        <>
          <button
            type="button"
            onClick={onClear}
            aria-label={clearLabel}
            className="ml-auto flex shrink-0 items-center px-1 text-ui-text-primary-text hover:text-ui-cta-primary"
          >
            <Icon icon={ic_close} size={18} />
          </button>
          {wand && (
            <>
              <span className={CHIP_DIVIDER} aria-hidden="true" />
              {wand}
            </>
          )}
        </>
      )}
    </div>
  );
}

// A field whose value is a complex jq expression the builder can't edit:
// a Σ marker, the expression (truncated, full text on hover) and the wand,
// which the caller passes in disabled. Edited in Code mode only.
export function LockedChip({
  expression,
  wand,
}: {
  expression: string;
  wand?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const hasValue = expression.trim().length > 0;
  return (
    // The hover title and accessible name say why the field can't be edited
    // here; the Σ chip itself carries no visible caption.
    <div
      className={CHIP}
      data-testid="JqFilterBuilder-locked-chip"
      role="group"
      aria-label={t('Edit in Code mode')}
      title={
        hasValue
          ? `${expression}\n\n${t('Edit in Code mode')}`
          : t('Edit in Code mode')
      }
    >
      <span
        aria-hidden="true"
        className="shrink-0 font-sans text-body-sm font-bold text-ui-text-secondary-text"
      >
        Σ
      </span>
      <span className="min-w-0 flex-1 truncate text-ui-text-secondary-text">
        {hasValue ? expression : t('Not mapped')}
      </span>
      {wand && (
        <>
          <span className={CHIP_DIVIDER} aria-hidden="true" />
          {wand}
        </>
      )}
    </div>
  );
}

// An unmapped slot: dashed box, "Drop a source field" placeholder, wand on the
// right. The placeholder area is the drop/click target (role=button); the wand
// and any `extra` control sit beside it so no button nests inside a button.
export function EmptySlot({
  label,
  isOver,
  sourceArmed,
  readOnly,
  handlers,
  onActivate,
  onDropPath,
  wand,
  extra,
}: {
  label: string;
  isOver: boolean;
  sourceArmed: boolean;
  readOnly: boolean;
  handlers: DragOverHandlers;
  onActivate: (el: HTMLElement) => void;
  onDropPath: () => void;
  wand?: React.ReactNode;
  extra?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const text = isOver
    ? t('Drop here')
    : sourceArmed
      ? t('Click to map here')
      : label;
  // Idle text is gray; the armed/over states keep their teal.
  const tone = isOver
    ? `slot-dashed-hot ${DROP_ZONE_ACTIVE}`
    : sourceArmed
      ? `slot-dashed-hot ${DROP_ZONE_TARGET_HOT}`
      : `slot-dashed ${DROP_ZONE_IDLE} !text-greyscale-5`;
  return (
    // data-empty-slot: the keyboard/click pick arms this wrapper (it carries
    // the dashed outline). The ring shows when the inner target has focus.
    <div
      data-empty-slot
      data-testid="JqFilterBuilder-empty-slot"
      className={`${EMPTY_SLOT} ${tone} [&:has(>[data-slot-target]:focus-visible)]:ring-2 [&:has(>[data-slot-target]:focus-visible)]:ring-ui-cta-primary`}
    >
      <div
        data-slot-target
        {...handlers}
        tabIndex={readOnly ? undefined : 0}
        role={readOnly ? undefined : 'button'}
        aria-label={readOnly ? undefined : text}
        onKeyDown={
          readOnly
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onActivate(e.currentTarget);
                }
              }
        }
        onClick={readOnly ? undefined : (e) => onActivate(e.currentTarget)}
        onDrop={
          readOnly
            ? undefined
            : (e) => {
                e.preventDefault();
                onDropPath();
              }
        }
        className={`${EMPTY_SLOT_TEXT} ${readOnly ? '' : 'cursor-pointer'}`}
      >
        {text}
      </div>
      {!readOnly && (extra || wand) && (
        <div className="flex shrink-0 items-center pr-1.5">
          {extra}
          {wand && (
            <>
              <span className={CHIP_DIVIDER} aria-hidden="true" />
              {wand}
            </>
          )}
        </div>
      )}
    </div>
  );
}
